import { takeLatest } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects'; // call, put, select, take
import createWebAPI from './WebAPI';
import createThrottler from './throttler';

const downloadsKeywordThrottler = createThrottler({ delay: 2000 });

const GET_MORE_DOWNLOADS = 'user-dashboard/downloads/GET_MORE_DOWNLOADS';
export const getMoreDownloads = (options = {}) => ({ type: GET_MORE_DOWNLOADS, options });

const CLEAR_DOWNLOADS = 'user-dashboard/downloads/CLEAR_DOWNLOADS';
export const clearDownloads = () => ({ type: CLEAR_DOWNLOADS });

const SET_DOWNLOADS_LOADED = 'user-dashboard/downloads/SET_DOWNLOADS_LOADED';
export const setDownloadsLoaded = value => ({ type: SET_DOWNLOADS_LOADED, value });

const SET_DOWNLOADS_ALL_LOADED = 'user-dashboard/downloads/SET_DOWNLOADS_ALL_LOADED';
export const setDownloadsAllLoaded = value => ({ type: SET_DOWNLOADS_ALL_LOADED, value });

const LOAD_DOWNLOADS_PARTIAL_RESULT = 'user-dashboard/downloads/LOAD_DOWNLOADS_PARTIAL_RESULT';
export const loadDownloadsPartialResult = items => ({ type: LOAD_DOWNLOADS_PARTIAL_RESULT, items });

const SET_DOWNLOADS_LICENCE_FILTER = 'user-dashboard/downloads/SET_DOWNLOADS_LICENCE_FILTER';
export const setDownloadsLicenceFilter = value => ({ type: SET_DOWNLOADS_LICENCE_FILTER, value });

const SET_DOWNLOADS_KEYWORD_FILTER = 'user-dashboard/downloads/SET_DOWNLOADS_KEYWORD_FILTER';
export const setDownloadsKeywordFilter = value => ({ type: SET_DOWNLOADS_KEYWORD_FILTER, value });

const initialState = {
  downloads: [],
  downloadsPagesLoaded: 0,
  downloadsToShow: 10,
  downloadsAllLoaded: false,
  downloadsLoaded: false,
  downloadsKeywordFilter: '',
  previousDownloadsKeywordFilter: '',
  downloadsLicenceFilter: { label: 'All', value: 'all' },
};

const webApi = createWebAPI();

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_DOWNLOADS_LOADED: {
      return { ...state, downloadsLoaded: action.value };
    }
    case SET_DOWNLOADS_ALL_LOADED: {
      return { ...state, downloadsAllLoaded: action.value };
    }
    case LOAD_DOWNLOADS_PARTIAL_RESULT: {
      if (action.items) {
        return { ...state, downloads: state.downloads.concat(action.items) };
      }
      return { ...state, downloads: state.downloads.concat([]) };
    }
    case CLEAR_DOWNLOADS: {
      return {
        ...state,
        downloads: [],
        downloadsPagesLoaded: 0,
        downloadsAllLoaded: false,
      };
    }
    case SET_DOWNLOADS_LICENCE_FILTER: {
      return { ...state, downloadsLicenceFilter: action.value };
    }
    case SET_DOWNLOADS_KEYWORD_FILTER: {
      return {
        ...state,
        downloadsKeywordFilter: action.value,
        previousDownloadsKeywordFilter: state.downloadsKeywordFilter,
      };
    }
    default:
      return state;
  }
}

export function sagas({ store }) {
  function* getMoreDownloadsSaga(action) {
    try {
      const { downloadsLoaded, downloadsToShow, downloads, downloadsAllLoaded, downloadsLicenceFilter, downloadsKeywordFilter } = yield select((state) => ({
        downloadsLoaded: state.downloadsModule.downloadsLoaded,
        downloadsToShow: state.downloadsModule.downloadsToShow,
        downloads: state.downloadsModule.downloads,
        downloadsAllLoaded: state.downloadsModule.downloadsAllLoaded,
        downloadsKeywordFilter: state.downloadsModule.downloadsKeywordFilter,
        downloadsLicenceFilter: state.downloadsModule.downloadsLicenceFilter,
      }));
      if (downloadsLoaded && downloadsAllLoaded) return;
      const params = {
        skip: downloads.length,
        limit: downloadsToShow,
      };
      if (downloadsKeywordFilter && !action.options.ignoreKeyword) params.keyword = downloadsKeywordFilter;
      if (downloadsLicenceFilter.value !== 'all') params.licence = downloadsLicenceFilter.value;

      yield put(setDownloadsLoaded(false));
      const result = yield call(() => webApi.downloads.getDownloadedResources(params));

      if (result.length <= 0) {
        yield put(setDownloadsAllLoaded(true));
      }
      yield put(loadDownloadsPartialResult(result));

      yield put(setDownloadsLoaded(true));
      if (result.length < downloadsToShow) yield put(setDownloadsAllLoaded(true));
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error('Exception in get more downloads saga', ex);
    }
  }

  function* setDownloadsLicenceFilterSaga() {
    yield put(clearDownloads());
    yield put(getMoreDownloads());
  }

  function* setDownloadsKeywordFilterSaga() {
    const { downloadsKeywordFilter, previousDownloadsKeywordFilter } = yield select((state) => ({
      downloadsKeywordFilter: state.downloadsModule.downloadsKeywordFilter,
      previousDownloadsKeywordFilter: state.downloadsModule.previousDownloadsKeywordFilter,
    }));

    const deletedThirdChar = downloadsKeywordFilter.length < 3 && previousDownloadsKeywordFilter.length >= 3;
    const shouldCallApi = downloadsKeywordFilter.length > 2 || deletedThirdChar;

    if (shouldCallApi) {
      downloadsKeywordThrottler(() => {
        store.dispatch(clearDownloads());
        store.dispatch(getMoreDownloads({ ignoreKeyword: downloadsKeywordFilter.length < 3 }));
      });
    }
  }

  return {
    * watchGetMoreDownloads() { yield* takeLatest(GET_MORE_DOWNLOADS, getMoreDownloadsSaga); },
    * watchSetDownloadsLicenceFilter() { yield* takeLatest(SET_DOWNLOADS_LICENCE_FILTER, setDownloadsLicenceFilterSaga); },
    * watchSetDownloadsKeywordFilter() { yield* takeLatest(SET_DOWNLOADS_KEYWORD_FILTER, setDownloadsKeywordFilterSaga); },
  };
}
